import { t, useT } from '../i18n'
import { dayRange } from '../domain/limits'
import { useDomainLimits } from '../domain/useDomainLimits'
import {
  dayOffset,
  dateFromOffset,
  clampOffset,
  inRange,
  humanDate as human,
} from './driftDay'

interface DayTimelineProps {
  /** Выбранная дата раздела (YYYY-MM-DD). */
  date: string
  /** Сегодня (YYYY-MM-DD) — нуль оси. */
  today: string
  onChange: (date: string) => void
  /** Дата последних течений — дальше неё сноса ещё нет. */
  currentsThrough?: string | null
  /** Состояние слоя стрелок: почему их не видно (null — всё в порядке). */
  notice?: string | null
}

/** Подпись смещения: 0 — «сегодня», ±1 — вчера/завтра, дальше — «−5 сут». */
function offsetLabel(n: number): string {
  if (n === 0) return t('models.day.today')
  if (n === -1) return t('models.day.yesterday')
  if (n === 1) return t('models.day.tomorrow')
  if (n === 2) return t('models.day.dayAfterTomorrow')
  return t('common.daysShort', { count: n })
}

/** Подпись края оси: «−7», «+2», «0». */
function edgeLabel(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0'
}

/** Перемотка картины сноса по дням: −7 … +2 от сегодня (домен вправе сузить).
 *
 *  Ось одна: слайдер двигает и косяки, и фоновый слой течений. Сеть при этом
 *  не дёргается — позиция на день это срез уже загруженного трека
 *  (см. docs/plans/2026-07-20-drift-day-slider-design.md). */
export function DayTimeline({ date, today, onChange, currentsThrough, notice }: DayTimelineProps) {
  const tr = useT()
  const range = dayRange(useDomainLimits().period)
  const offset = dayOffset(date, today)
  const outside = !inRange(offset, range)
  const value = clampOffset(offset, range)

  // Докуда реально доведён снос — за этой границей точки станут полыми.
  const throughOffset = currentsThrough ? dayOffset(currentsThrough, today) : null
  const ahead = throughOffset != null && value > throughOffset

  return (
    <div className="pointer-events-auto w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-hull bg-abyss/95 px-3 py-2 shadow-console backdrop-blur">
      <div className="flex items-baseline justify-between">
        <span className="tnum text-sm font-semibold text-foam">{human(date)}</span>
        <span className="text-xs text-mist">
          {outside ? tr('models.day.outsideRange') : offsetLabel(offset)}
        </span>
      </div>

      <input
        type="range"
        min={range.min}
        max={range.max}
        step={1}
        value={value}
        onChange={e => onChange(dateFromOffset(Number(e.target.value), today))}
        aria-label={tr('models.day.sliderAria')}
        className="mt-1.5 w-full accent-[rgb(var(--accent))]"
      />

      <div className="tnum flex justify-between text-[10px] text-faint">
        <span>{edgeLabel(range.min)}</span>
        <button
          type="button"
          onClick={() => onChange(today)}
          className="rounded px-1 text-mist hover:text-foam"
        >
          {tr('models.day.today')}
        </button>
        <span>{edgeLabel(range.max)}</span>
      </div>

      {/* Слой стрелок молчать не должен: пустая карта неотличима от сбоя. */}
      {notice && <div className="mt-1 text-[11px] leading-snug text-buoy">{notice}</div>}

      {/* Честная пометка: за концом течений снос не посчитан, точки полые. */}
      {ahead && (
        <div className="mt-1 text-[11px] leading-snug text-buoy">
          {tr('models.day.currentsThrough', { date: human(currentsThrough!) })}
        </div>
      )}
    </div>
  )
}
